import { BudgetAllowance } from '@frontend/budget/types';
import { Certification } from '@frontend/certificate/types';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton, Spinner } from '@frontend/components/common';
import { IEquipment } from '@frontend/equipment/types';
import { CommonMessage } from '@frontend/lang';
import { IProduct, ProductType } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import { StockAvailability, StockReplenishmentExtended } from '@frontend/stock/types';

import { useProductCard } from './product-card.controller';

type EquipmentWithTag = IEquipment & {
    tag?: { name?: string | null } | null;
    equipment_tag?: { name?: string | null } | null;
};

const getReplenishmentColor = (replenishment: StockReplenishmentExtended | undefined): string => {
    if (!replenishment) return '';
    if (replenishment.quantity < replenishment.required_min) return 'bg-status-danger';
    if (replenishment.filled_max_percentage >= 100) return 'bg-status-success';
    return 'bg-status-warning';
};

export interface ProductCardProps {
    product?: IProduct;
    product_id?: string;
    onClick?: (product: IProduct) => void;
    equipment?: IEquipment;
    action?: {
        text: React.ReactNode;
        onClick: () => void;
    };
    variant?: 'default' | 'attributes' | 'stock';
    layout?: 'horizontal' | 'vertical';
    fill_amount?: number;
    allowance?: BudgetAllowance | null;
    certification?: Certification | null;
    certificationLoading?: boolean;
    stock?: StockAvailability | null;
    hasStockInOtherSpots?: boolean;
    current_stock?: number;
    min_stock?: number;
    max_stock?: number;
    replenishment?: StockReplenishmentExtended;
    noImage?: boolean;
    description?: string | React.ReactNode;
    styles?: {
        container?: string;
    };
}

export const ProductCard = (props: ProductCardProps) => {
    const viewProps = useProductCard(props);

    if (!viewProps.product) {
        return (
            <div className='bg-background-surface outline-border-default inline-flex w-full items-start justify-start gap-3 self-stretch rounded-xl p-4 outline-1 -outline-offset-1'>
                <Spinner />
            </div>
        );
    }

    const showAttributes = props.variant === 'attributes' || props.variant === 'stock';
    const isEquipment = viewProps.product?.type === ProductType.EQUIPMENT;
    const showReusableBadge = (!props.variant || props.variant === 'default') && isEquipment && !props.equipment;
    const isVertical = props.layout === 'vertical';

    const isDisabled = viewProps.isDisabled;
    const disabledReason = viewProps.disabledReason;
    const equipment = props.equipment as EquipmentWithTag | undefined;

    const color = getReplenishmentColor(props.replenishment);

    return (
        <div
            onClick={(e) => {
                e.preventDefault();
                !isDisabled && props.onClick && props.onClick(viewProps.product!);
            }}
            className={`bg-background-surface border-border-default inline-flex w-full items-start justify-start gap-3 self-stretch rounded-xl border p-3 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${props.styles?.container ?? ''}`}>
            <div className='flex w-full flex-col gap-3'>
                <div className={`flex ${isVertical ? 'flex-col items-center' : 'flex-row gap-4'}`}>
                    {!props.noImage && (
                        <div className={`relative ${isVertical ? 'h-44 w-44' : 'h-20 w-20'}`}>
                            <img
                                className={`aspect-square h-full w-full ${isVertical ? '' : 'rounded-lg'} object-contain`}
                                src={viewProps.image ?? viewProps.placeholder}
                                alt=''
                            />
                        </div>
                    )}
                    <div className={`inline-flex flex-1 flex-col items-start justify-start gap-3 ${isVertical ? 'self-stretch' : ''}`}>
                        <div className='flex items-start justify-between gap-2 self-stretch'>
                            <div className='flex flex-col gap-1'>
                                <span className='text-foreground-default line-clamp-2 flex-1'>{new Product(viewProps.product).getDefaultName()}</span>
                                {equipment?.serial_number && (
                                    <span className='text-brand-muted line-clamp-2 flex-1 text-xs'>SN: {equipment.serial_number}</span>
                                )}
                                {viewProps.product.sku && <span className='text-brand-muted line-clamp-2 flex-1 text-xs'>{viewProps.product.sku}</span>}
                                {viewProps.product.ean && <span className='text-brand-muted line-clamp-2 flex-1 text-xs'>{viewProps.product.ean}</span>}
                            </div>
                            {props.fill_amount !== undefined && !props.replenishment && (
                                <DefaultBadge
                                    label={`${props.fill_amount}x`}
                                    styles={{
                                        container: {
                                            color: `${props.fill_amount === 0 ? 'bg-status-success' : (props.current_stock ?? 0) < (props.min_stock ?? 0) ? 'bg-status-danger' : 'bg-status-warning'}`
                                        }
                                    }}
                                />
                            )}
                        </div>
                        {showAttributes && (
                            <div className='flex flex-wrap gap-1'>
                                {viewProps.productAttributes.map((attribute) => (
                                    <div
                                        key={attribute.attributeName?.toString()}
                                        className='bg-border-default inline-flex h-6 min-w-6 items-center justify-center gap-1 rounded px-1'>
                                        <div className='text-foreground-default justify-start text-sm'>{attribute.attributeName}:</div>
                                        <div className='text-foreground-default justify-start text-sm'>{attribute.valueName}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {(showReusableBadge || disabledReason) && (
                            <div className='flex w-full flex-wrap items-center gap-1'>
                                {showReusableBadge && (
                                    <div className='bg-brand-secondary inline-flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                                            {CommonMessage.OBJECTS.REUSABLE.DEFAULT}
                                        </div>
                                    </div>
                                )}
                                {disabledReason && (
                                    <div className='bg-status-warning inline-flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>{disabledReason}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {props.replenishment && (
                    <div className='flex w-full flex-col gap-1'>
                        <div className='flex w-full items-center gap-2'>
                            <div className='text-foreground-default justify-start text-center text-sm font-medium'>
                                {props.replenishment.filled_max_percentage}%
                            </div>
                            <div className='bg-background-default relative h-2 flex-1 overflow-hidden rounded-full'>
                                <div
                                    className={`absolute inset-y-0 left-0 rounded-full ${color}`}
                                    style={{ width: `${props.replenishment.filled_max_percentage}%` }}
                                />
                            </div>
                            <DefaultBadge
                                label={`${props.replenishment.proposed_max}x`}
                                styles={{
                                    container: { color }
                                }}
                            />
                        </div>
                    </div>
                )}
                {props.description}
                {/**
                 * TODO: Add due date of equipment when reservation service is implemented
                 */}
                {props.action && (
                    <DefaultPrimaryButton
                        text={props.action?.text}
                        onClick={props.action?.onClick}
                    />
                )}
            </div>
        </div>
    );
};
